import type { Kit, PartNode, ProductAPIResponse, ProductFeature } from '@partful/shared-types';
import { PartPathWithHash } from 'src/services/partPath';
import { PostProcessing } from 'src/services/productInventory';

interface ProductProps {
  product: ProductAPIResponse;
  partPaths?: PartPathWithHash[];
  toggleVariantNames?: (previouslySelected: string[], selected: string) => void;
  hiddenVariantNames?: string[];
}

interface ConfigurationApiResponse {
  productExternalID?: string; // Optional as we transition from productId
  configurationExternalID?: string; // Optional as we transition from productId
  colourMode?: 'GREYSCALE' | 'COLOUR'; // Optional as we transition to configuration Management
  postProcessing?: PostProcessing;
}

type ProductResponse = ProductAPIResponse & ConfigurationApiResponse;

export class Product {
  public partPaths: PartPathWithHash[];

  public currentProduct: ProductResponse;

  public toggleVariantNames: (previouslySelected: string[], selected: string) => void;

  public hiddenVariantNames?: string[];

  get productId(): string {
    return this.currentProduct.productId;
  }

  get name(): string | undefined {
    return this.currentProduct.name;
  }

  // The name of the root node of the part tree, used as configuration name
  get configurationName(): string {
    return this.currentProduct.bom?.name ?? '';
  }

  get bom(): PartNode | undefined {
    return this.currentProduct.bom;
  }

  get enabledFeatures(): ProductFeature[] | undefined {
    return this.currentProduct.enabledFeatures;
  }

  get parentProductIds(): string[] | undefined {
    return this.currentProduct.parentProductIds;
  }

  get colourMode(): 'GREYSCALE' | 'COLOUR' {
    return this.currentProduct.colourMode ?? 'COLOUR';
  }

  get postProcessingMode(): PostProcessing {
    return this.currentProduct.postProcessing ?? PostProcessing.ADVANCED;
  }

  get kits(): Kit[] {
    return this.currentProduct.bom?.kits ?? [];
  }

  getPartsById: (id: string) => PartPathWithHash[] = (id) => this.partPaths.filter((path) => path.part.id === id);

  getPartsByPartNumber: (partNumber: string) => PartPathWithHash[] = (partNumber) =>
    this.partPaths.filter((path) => path.part.partNumber === partNumber);

  constructor({ product, partPaths = [], toggleVariantNames = () => null, hiddenVariantNames = [] }: ProductProps) {
    this.currentProduct = product;
    this.partPaths = partPaths;
    this.toggleVariantNames = toggleVariantNames;
    this.hiddenVariantNames = hiddenVariantNames;
  }
}
